import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { signInWithPopup, signOut, onAuthStateChanged } from 'firebase/auth';
import { auth, provider } from '../firebase';
import { getApiUrl } from '../config/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userRole, setUserRole] = useState(null);

  // Initialize authentication state from Firebase
  useEffect(() => {
    const initializeAuth = async () => {
      setLoading(true);
      
      const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
        if (firebaseUser) {
          try {
            // Get Firebase ID token
            const idToken = await firebaseUser.getIdToken();
            setToken(idToken);

            // Get user role from backend
            const roleResponse = await fetch(getApiUrl('/api/auth/role'), {
              method: 'GET',
              headers: {
                'Authorization': `Bearer ${idToken}`
              }
            });
            
            let role = 'user'; // Default role
            if (roleResponse.ok) {
              const roleData = await roleResponse.json();
              role = roleData.role || 'user';
            } else {
              console.error('❌ Role API error:', roleResponse.status, await roleResponse.text());
            }
            
            setUser({
              id: firebaseUser.uid,
              username: firebaseUser.displayName || firebaseUser.email,
              email: firebaseUser.email,
              photoURL: firebaseUser.photoURL,
              role: role
            });
            setUserRole(role);
            console.log('🔐 AuthContext - User role set to:', role);
          } catch (err) {
            console.error('Error getting user role:', err);
            // Set default user with basic info
            setUser({
              id: firebaseUser.uid,
              username: firebaseUser.displayName || firebaseUser.email,
              email: firebaseUser.email,
              photoURL: firebaseUser.photoURL,
              role: 'user'
            });
            setUserRole('user');
          }
        } else {
          setUser(null);
          setToken(null);
          setUserRole(null);
        }
        
        setLoading(false);
      });

      return () => unsubscribe();
    };

    initializeAuth();
  }, []);

  // Login function - now uses Firebase Google Auth with popup
  const login = useCallback(async () => {
    console.log('🚀 Login button clicked - initiating Firebase popup...');
    setLoading(true);
    setError(null);

    try {
      const result = await signInWithPopup(auth, provider);
      console.log('✅ Login successful:', result.user.email);
      // The onAuthStateChanged listener will handle the rest
      return { success: true };
    } catch (err) {
      console.error('❌ Login error:', err);
      const errorMessage = err.message || 'Login failed';
      setError(errorMessage);
      setLoading(false);
      return { success: false, error: errorMessage };
    }
  }, []);

  // Logout function
  const logout = useCallback(async () => {
    try {
      await signOut(auth);
      // The onAuthStateChanged listener will handle the rest
    } catch (err) {
      console.error('Logout error:', err);
      setError(err.message);
    }
  }, []);

  // Check if user is admin
  const isAdmin = useCallback(() => {
    const isAdminResult = userRole === 'admin';
    console.log('🔍 isAdmin() called - userRole:', userRole, 'result:', isAdminResult);
    return isAdminResult;
  }, [userRole]);

  // Check if user is authenticated
  const isAuthenticated = useCallback(() => {
    return !!(token && user);
  }, [token, user]);

  const value = {
    user,
    token,
    loading,
    error,
    login,
    logout,
    isAdmin,
    isAuthenticated
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
